import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSettings } from "@/contexts/SettingsContext";
import { formatCurrency, formatDate } from "@/lib/formatters";
import { getAuthHeaders } from "@/lib/api";
import { setMetaDescription, setCanonicalUrl, setOpenGraphTags, setTwitterCardTags, setMetaRobots } from "@/lib/seo";
import { generateWebPageSchema, generateBreadcrumbListSchema, addSchemaToPage } from "@/lib/schema";
import { Pencil, Trash2, Info } from "lucide-react";
import { ClickableTooltip } from "@/components/ui/tooltip";

type Income = {
  id: number;
  amount: number;
  source: string;
  date: string;
  frequency?: string; // 'Monthly' or 'Weekly', defaults to 'Monthly'
};

export default function IncomePage() {
  useEffect(() => {
    const title = "Income - Munthly";
    const description = "Add and manage your income sources with Munthly's free budgeting tool. Track monthly earnings and organise your finances effectively.";
    
    document.title = title;
    setMetaDescription(description);
    const canonicalCleanup = setCanonicalUrl("/income");
    const ogCleanup = setOpenGraphTags(title, description, "/income");
    const twitterCleanup = setTwitterCardTags(title, description);
    const robotsCleanup = setMetaRobots("noindex, nofollow"); // Protected page - don't index
    
    // Add schema for income page
    const webPageSchema = generateWebPageSchema(
      "/income",
      title,
      description
    );
    const breadcrumbSchema = generateBreadcrumbListSchema([
      { name: "Home", url: "/" },
      { name: "Dashboard", url: "/dashboard" },
      { name: "Income", url: "/income" }
    ]);
    
    const schemaCleanup = addSchemaToPage([webPageSchema, breadcrumbSchema]);
    
    return () => {
      canonicalCleanup();
      ogCleanup();
      twitterCleanup();
      robotsCleanup();
      schemaCleanup();
    };
  }, []);
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [amount, setAmount] = useState("");
  const [source, setSource] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [frequency, setFrequency] = useState<"Monthly" | "Weekly">("Monthly");
  const [editingId, setEditingId] = useState<number | null>(null);
  const { settings } = useSettings();
  const { getToken } = useAuth();

  useEffect(() => {
    fetchIncome();
  }, []);

  const fetchIncome = async () => {
    const res = await fetch("/api/v1/income", {
      headers: await getAuthHeaders(getToken)
    });
    const data = await res.json();
    setIncomes(data);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || !source || !date) return;

    if (editingId) {
      // Update existing
      const res = await fetch(`/api/v1/income/${editingId}`, {
        method: "PUT",
        headers: await getAuthHeaders(getToken),
        body: JSON.stringify({ amount: parseFloat(amount), source, date, frequency }),
      });
      if (!res.ok) {
        alert("Failed to update income");
        return;
      }
      setEditingId(null);
    } else {
      // Create new
      const res = await fetch("/api/v1/income", {
        method: "POST",
        headers: await getAuthHeaders(getToken),
        body: JSON.stringify({ amount: parseFloat(amount), source, date, frequency }),
      });
      if (!res.ok) {
        alert("Failed to add income");
        return;
      }
    }

    setAmount("");
    setSource("");
    setFrequency("Monthly");
    fetchIncome();
  };

  const handleEdit = (inc: Income) => {
    setAmount(inc.amount.toString());
    setSource(inc.source);
    setDate(inc.date);
    setFrequency((inc.frequency || "Monthly") as "Monthly" | "Weekly");
    setEditingId(inc.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    setAmount("");
    setSource("");
    setFrequency("Monthly");
    setEditingId(null);
  }

  const handleDelete = async (id: number) => {
    await fetch(`/api/v1/income/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    fetchIncome();
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-heading text-2xl sm:text-3xl font-semibold tracking-tight">Income</h1>
        <p className="text-sm text-muted-foreground mt-1">Track your income sources</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle>{editingId ? "Edit Income" : "Add Income"}</CardTitle>
            <ClickableTooltip
              content={
                <div>
                  <p>Add your regular income amount on the day it should arrive. Choose Monthly or Weekly frequency. For monthly income, Munthly will assume you receive this income on the same date each month. For weekly income, Munthly will calculate how many payments occur in each month and automatically break it down into your monthly balance sheet.</p>
                </div>
              }
            >
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                aria-label="How income works"
              >
                <Info className="w-4 h-4" />
              </button>
            </ClickableTooltip>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-4">
              <div className="space-y-2">
                <Label htmlFor="source">Source</Label>
                <Input
                  type="text"
                  id="source"
                  placeholder="e.g. Salary"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Amount</Label>
                <Input
                  type="number"
                  id="amount"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="frequency">Frequency</Label>
                <Select value={frequency} onValueChange={(value) => setFrequency(value as "Monthly" | "Weekly")}>
                  <SelectTrigger id="frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                    <SelectItem value="Weekly">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Next Payment Date</Label>
                <Input
                  type="date"
                  id="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              {editingId && (
                <Button type="button" variant="outline" onClick={handleCancel}>Cancel</Button>
              )}
              <Button type="submit" disabled={!amount || !source || !date}>
                {editingId ? "Update Income" : "Add Income"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Income History</CardTitle>
        </CardHeader>
        <CardContent>
          {incomes.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No income recorded yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {incomes.map((inc) => (
                <div key={inc.id} className="flex justify-between items-center p-4 rounded-2xl bg-[var(--surface-container)] hover:bg-[var(--surface-container-high)] transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-base">{inc.source}</p>
                      <span className="text-xs bg-[var(--surface-container-high)] text-muted-foreground px-2.5 py-1 rounded-full font-medium">
                        {inc.frequency || "Monthly"}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">{formatDate(inc.date, settings.dateFormat)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-lg" style={{ color: 'var(--success)' }}>
                      +{formatCurrency(inc.amount, settings.currencySymbol)}
                    </span>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon-sm" onClick={() => handleEdit(inc)}>
                        <Pencil className="w-4 h-4" />
                        <span className="sr-only">Edit</span>
                      </Button>
                      <Button variant="ghost" size="icon-sm" onClick={() => handleDelete(inc.id)} className="text-destructive hover:text-destructive">
                        <Trash2 className="w-4 h-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
